/**
 * @file SSE 事件流
 */
import {Chunk} from '../constants/mock';
import {caseUBCLog} from '../log';
import {delay} from './tools';

interface OPTIONS {
    headers?: Record<string, string>;
    payload?: string;
    method?: string;
    withCredentials?: boolean;
    timeout?: {
        connect: number;
        interval: number;
    };
}

interface EventStreamResult {
    [key: string]: string | null ;
    id: string | null ;
    data: string;
    retry: string | null;
    event: string;
}

/**
 * sse 建联超时时间
 */
const SSE_CONNECT_TIMEOUT = 20 * 1000; // 20s

/**
 * sse chunk间的超时时间
 */
const SSE_CHUNK_ANSWER_TIMEOUT = 100 * 1000; // 100s

export enum SSEStatus {
    // CONNECTING = XMLHttpRequest.UNSENT,
    // OPENED = XMLHttpRequest.OPENED,
    // HEADERS_RECEIVED = XMLHttpRequest.HEADERS_RECEIVED,
    // LOADING = XMLHttpRequest.LOADING,
    // DONE = XMLHttpRequest.DONE,
    // CLOSED = 10000,
    CONNECTING = 0, // XMLHttpRequest.UNSENT,
    OPENED = 1, // XMLHttpRequest.OPENED,
    CLOSED = 4, // XMLHttpRequest.DONE,
}

export enum DataServer {
    MOCKSSE = 'mocksse',
    SSE = 'sse',
    LOCALSSE = 'localsse',
}

export class EventObserver {
    protected readonly listeners: Record<string, Array<(e: CustomEvent) => void>> = {};

    /**
     * 增加事件监听
     * @param type 监听的事件类型名称，一般是 'message'
     * @param listener 事件处理函数
     */
    addEventListener(type: string, listener: (e: CustomEvent) => void): void {
        if (!this.listeners[type]) {
            this.listeners[type] = [];
        }
        if (!this.listeners[type].includes(listener)) {
            this.listeners[type].push(listener);
        }
    }

    dispatchEvent = (e: CustomEvent | null) => {
        if (!e || !this.listeners[e.type]) {
            return;
        }
        this.listeners[e.type].every(callback => {
            callback(e);
        });
    };

    /**
     * 移除事件监听
     * @param type 移除的事件类型名称
     * @param listener 移除的事件监听函数
     */
    removeEventListener(type: string, listener?: (e: CustomEvent) => void): void {
        if (type === 'all') {
            Object.keys(this.listeners).forEach(t => delete this.listeners[t]);
            return;
        }
        if (!this.listeners[type]) {
            return;
        }
        this.listeners[type] = this.listeners[type].filter(l => l !== listener);
        if (this.listeners[type].length === 0) {
            delete this.listeners[type];
        }
    }

    close() {
        this.removeEventListener('all');
    }
}

export class SSE extends EventObserver {
    private readonly url: string;
    private readonly headers: Record<string, string>;
    private readonly method: string;
    private readonly payload: string;
    private readonly withCredentials: boolean;
    private readonly FIELD_SEPARATOR = ':';
    private readonly timeout = {
        connect: SSE_CONNECT_TIMEOUT,
        interval: SSE_CHUNK_ANSWER_TIMEOUT,
    };
    private readonly RETRY_LIMIT = 2; // 最大重试次数
    private progress = 0;
    private chunk = '';
    private xhr: XMLHttpRequest | null = null;
    private retry = 0;
    private timer: NodeJS.Timeout | number = 0;
    private readyState = SSEStatus.CLOSED;

    constructor(url: string, options: OPTIONS = {}) {
        super();
        const {headers = {}, payload = '', method, withCredentials} = options;
        this.url = url;
        this.headers = headers;
        this.payload = payload;
        this.method = method || (this.payload ? 'POST' : 'GET');
        this.withCredentials = !!withCredentials;
        if (options.timeout) {
            this.timeout = options.timeout;
        }
    }

    /**
     * @description 发送post请求，开启sse链接
     */
    stream() {
        this._setReadyState(SSEStatus.CONNECTING);

        this.xhr = new XMLHttpRequest();
        // this.xhr.timeout = 10 * 60 * 1000; // ms，请求开始到结束的总时间
        this.xhr.addEventListener('progress', this._onStreamProgress.bind(this));
        this.xhr.addEventListener('load', this._onStreamLoaded.bind(this));
        this.xhr.addEventListener('readystatechange', this._onReadyStateChange.bind(this));
        this.xhr.addEventListener('error', this._onStreamError.bind(this));
        this.xhr.addEventListener('abort', this._onStreamAbort.bind(this));
        this.xhr.addEventListener('timeout', this._onStreamTimeout.bind(this));
        this.xhr.open(this.method, this.url);

        for (const header in this.headers) {
            if (header) {
                this.xhr.setRequestHeader(header, this.headers[header]);
            }
        }

        this.xhr.withCredentials = this.withCredentials;
        this.xhr.send(this.payload);

        this._checkConnectTimeout();
    };

    /**
     * @description 关闭sse链接
     */
    close() {
        clearTimeout(this.timer);

        if (this.readyState === SSEStatus.CLOSED) {
            return;
        }

        this._setReadyState(SSEStatus.CLOSED);

        this.xhr?.abort();
        this.xhr = null;
    };

    private _setReadyState(state: number) {
        const event = new CustomEvent('readystatechange', {
            detail: {
                readyState: state,
            },
        });
        this.readyState = state;
        this.dispatchEvent(event);
    };

    private _onStreamError(e: ProgressEvent<XMLHttpRequestEventTarget>) {
        // 抽样异常数据
        if (Math.random() < 0.5) {
            caseUBCLog({
                opName: 'SSE_ERROR',
                msg: JSON.stringify(e),
            });
        }
        const event = new CustomEvent('error', {
            detail: {
                // eslint-disable-next-line max-len
                message: `xhr: ${this.xhr?.status} ${this.xhr?.statusText || e.type}, ${e.loaded}/${e.total} bytes transferred`,
            },
        });
        this.dispatchEvent(event);
        this.close();
    };

    /**
     * error 时也会触发 abort
     */
    private _onStreamAbort(e: ProgressEvent<XMLHttpRequestEventTarget>) {
        const event = new CustomEvent('abort', {
            detail: {
                // eslint-disable-next-line max-len
                message: `xhr: ${this.xhr?.status || 0} ${this.xhr?.statusText || e.type}, ${e.loaded}/${e.total} bytes transferred`,
            },
        });
        this.dispatchEvent(event);
        this.close();
    };

    private _onStreamTimeout(e: ProgressEvent) {
        const event = new CustomEvent('timeout', {
            detail: {
                type: e.type,
                // eslint-disable-next-line max-len
                message: `xhr: ${this.xhr?.status} ${this.xhr?.statusText || e.type}, ${e.loaded}/${e.total} bytes transferred`,
                retry: this.retry,
            },
        });
        this.dispatchEvent(event);
        this.close();
    };

    private _onStreamProgress(e: ProgressEvent<XMLHttpRequestEventTarget>) {
        if (!this.xhr) {
            return;
        }

        if (this.xhr.status !== 200) {
            this._onStreamError(e);
            return;
        }

        if (this.readyState === SSEStatus.CONNECTING) {
            this._setReadyState(SSEStatus.OPENED);
        }
        let stream = this.xhr.response.substring(this.progress);
        this.progress += stream.length;
        if (stream.startsWith('\nevent')) {
            stream = '\n' + stream;
        }

        // stream 可能是由多个 `data: AAA\n\n data:BBB` 组成，BBB 可能不是一个完整的 json，需要等待下一段数据拼接
        stream.split(/(\r\n|\r|\n){2}/g).forEach((part: string) => {
            if (part.trim().length === 0) {
                this.dispatchEvent(this._parseEventChunk(this.chunk.trim()));
                this.chunk = '';
            }
            else {
                this.chunk += part;
            }
        });
    };

    /**
     * 正常/异常断开情况下，xhr.readyState === XMLHttpRequest.DONE，status: 200，statusText: 'OK'
     * 因此，在断开时进行超时检测
     */
    private _onStreamLoaded() {
        clearTimeout(this.timer);

        if (this.xhr?.readyState === XMLHttpRequest.DONE) {
            this._checkIntervalTimeout();
        }
    };

    /**
     * Parse a received SSE event chunk into a constructed event object.
     * 数据样例：https://developer.mozilla.org/zh-CN/docs/Web/API/Server-sent_events/Using_server-sent_events
     */
    private _parseEventChunk(chunk: string) {
        if (!chunk || chunk.length === 0) {
            return null;
        }

        const e: EventStreamResult = {
            id: null,
            retry: null,
            data: '',
            event: 'message',
        };

        chunk.split(/\n|\r\n|\r/).forEach((line: string) => {
            line = line.trim();
            const index = line.indexOf(this.FIELD_SEPARATOR);
            if (index <= 0) {
                // Line was either empty, or started with a separator and is a comment.
                // Either way, ignore.
                return;
            }

            const field = line.substring(0, index);
            if (!(field in e)) {
                return;
            }

            const value = line.substring(index + 1).trimStart();
            if (field === 'data') {
                e[field] += value;
            }
            else {
                e[field] = value;
            }
        });

        const event = new CustomEvent(e.event, {
            detail: {
                data: e.data,
            },
        });

        return event;
    }

    private _onReadyStateChange() {
        clearTimeout(this.timer);

        if (!this.xhr) {
            return;
        }

        // @ts-ignore
        this.xhr.updatetime = Date.now();

        if (this.xhr.readyState === XMLHttpRequest.DONE) {
            // this._setReadyState(SSEStatus.CLOSED);
        }
        else {
            this._checkIntervalTimeout();
        }
    }

    /**
     * 超时判断
     */
    private _checkConnectTimeout() {
        clearTimeout(this.timer);

        this.timer = setTimeout(async () => {
            if (!this.xhr || this.readyState === SSEStatus.CLOSED) {
                return;
            }
            // @ts-ignore
            if (+this.xhr.updatetime + this.timeout.connect > Date.now()) {
                return;
            }
            if (this.retry++ < this.RETRY_LIMIT) {
                this.close();
                await delay(1000);// 等待上一波请求结束
                this.stream();
            }
            else {
                this._onStreamTimeout(new ProgressEvent('connect_timeout'));
            }
        }, this.timeout.connect);
    }

    /**
     * 超时判断
     */
    private _checkIntervalTimeout() {
        this.timer = setTimeout(() => {
            if (!this.xhr || this.readyState === SSEStatus.CLOSED) {
                return;
            }
            // @ts-ignore
            if (+this.xhr.updatetime + this.timeout.interval <= Date.now()) {
                this._onStreamTimeout(new ProgressEvent('interval_timeout'));
            }
        }, this.timeout.interval);
    }
}

export class MockSSE extends EventObserver {
    private readonly chunks: Chunk[] = [];

    constructor(chunks: Chunk[]) {
        super();
        this.chunks = chunks;
    }

    async stream() {
        for (const chunk of this.chunks) {
            await delay(chunk.delay || 100);
            this.dispatchEvent(this._parseEventChunk(chunk));
        }
    }

    private _parseEventChunk(chunk: Chunk) {
        const event = new CustomEvent(chunk.event || 'message', {
            detail: {
                data: JSON.stringify(chunk),
            },
        });
        return event;
    }
}
